"use client";

import Image from "next/image";
import { canOptimizeImage } from "@/lib/image";
import {
  IconDisc,
  IconGuitarPick,
  IconMicrophone2,
  IconMusic,
  IconSparkles,
  IconStar,
  IconToolsKitchen2,
  IconUsersGroup,
} from "@tabler/icons-react";
import { cn } from "@/lib/utils";
import { Reveal } from "../../../components/cambodia-food-expo/ui/Reveal";
import type { EventPeriod, PerformanceIcon, ScheduleItem } from "./types";
import { BlockTitle, UI, useText } from "./shared";

const ICONS: Record<PerformanceIcon, typeof IconMusic> = {
  drum: IconUsersGroup,
  music: IconMusic,
  microphone: IconMicrophone2,
  guitar: IconGuitarPick,
  dance: IconSparkles,
  disc: IconDisc,
  star: IconStar,
  food: IconToolsKitchen2,
};

/** Featured banner for an evening period. `concert` renders the page's finale treatment. */
export function EventFeatureBanner({
  feature,
  concert = false,
}: {
  feature: NonNullable<EventPeriod["feature"]>;
  concert?: boolean;
}) {
  const { text, isKhmer } = useText();
  return (
    <div
      className={cn(
        "relative isolate overflow-hidden rounded-[22px] border",
        concert
          ? "border-[#f6cf63]/40 shadow-[0_30px_90px_rgba(246,207,99,0.18)]"
          : "border-white/10",
      )}
    >
      <div
        className={cn(
          "relative",
          concert
            ? "aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]"
            : "aspect-[16/10] sm:aspect-[21/9]",
        )}
      >
        <Image
          src={feature.image}
          unoptimized={!canOptimizeImage(feature.image)}
          alt={text(feature.title)}
          fill
          sizes="(min-width: 1280px) 1240px, 100vw"
          className="object-cover"
        />
        <div
          className={cn(
            "absolute inset-0",
            concert
              ? "bg-[radial-gradient(ellipse_at_center,rgba(43,15,86,0.35),rgba(3,10,30,0.92)_75%)]"
              : "bg-[linear-gradient(90deg,rgba(3,10,30,0.92),rgba(3,10,30,0.35)_70%,transparent)]",
          )}
        />
        {concert && (
          <div
            aria-hidden="true"
            className="absolute inset-x-0 bottom-0 h-1/2 bg-[radial-gradient(ellipse_at_bottom,rgba(246,207,99,0.35),transparent_65%)] motion-safe:animate-pulse"
          />
        )}
        <div
          className={cn(
            "absolute inset-0 flex flex-col justify-center p-6 md:p-12",
            concert ? "items-center text-center" : "items-start",
          )}
        >
          <p
            className={cn(
              "mb-3 inline-flex items-center gap-2 rounded-full border border-[#f6cf63]/40 bg-black/30 px-3.5 py-1 text-xs font-semibold text-[#f6cf63] backdrop-blur md:text-sm",
              !isKhmer && "uppercase tracking-[0.2em]",
            )}
          >
            <IconStar className="size-3.5" aria-hidden="true" />
            {text(feature.subtitle)}
          </p>
          <p
            className={cn(
              "font-black text-white drop-shadow-[0_6px_30px_rgba(0,0,0,0.5)]",
              concert
                ? cn(
                    // bg-clip-text only paints inside the box, so Khmer vowels and
                    // subscripts above/below the line need the taller line height
                    // and padding, or they are cut off.
                    "text-balance bg-[linear-gradient(180deg,#fff6d6,#f6cf63)] bg-clip-text px-1 py-[0.25em] text-[2.4rem] leading-[1.7] text-transparent sm:text-6xl lg:text-7xl",
                    !isKhmer && "uppercase tracking-[0.04em]",
                  )
                : "max-w-xl text-2xl leading-[1.35] md:text-4xl",
            )}
          >
            {text(feature.title)}
          </p>
        </div>
      </div>
    </div>
  );
}

/** Performance line-up as cards (artists aren't announced, so cards describe each act). */
export function EventPerformanceCards({ items }: { items: ScheduleItem[] }) {
  const { text } = useText();
  return (
    <div>
      <BlockTitle icon={<IconMusic className="size-5" aria-hidden="true" />}>
        {text(UI.lineup)}
      </BlockTitle>
      <ol className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((item, i) => {
          const Icon = ICONS[item.icon ?? "music"];
          return (
            <li key={`${item.title.en}-${i}`}>
              <Reveal
                delay={(i % 4) * 0.05}
                className="flex h-full items-start gap-3 rounded-[16px] border border-white/10 bg-white/[0.05] p-4 transition hover:border-[#f6cf63]/40 hover:bg-white/[0.08]"
              >
                <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[linear-gradient(135deg,#f6cf63,#e8a524)] text-[#041127]">
                  <Icon className="size-5" aria-hidden="true" />
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-white/50">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="block text-[0.95rem] font-medium leading-7 text-white">
                    {text(item.title)}
                  </span>
                </span>
              </Reveal>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
